// page-backlog-review-menus.ts — Les CTA à menu déroulant de l'entête de revue.
//
// Extrait de `page-backlog-review.ts` le 2026-09-13. Le rendu est pur ; le
// câblage ne connaît que des identifiants, et rend son écouteur `document` au
// routeur pour qu'il ne s'empile pas à chaque navigation.
//
// Globaux ambiants : esc, registerPageDisposer (core-globals.d.ts).

export interface MenuItem {
  id: string;
  label: string;
}

/** Un CTA à menu déroulant, sur le modèle du « Ajouter ▾ » de `#actions`.
 *
 * Les six sorties de la revue tenaient sur une seule rangée et se disputaient
 * la lecture ; regroupées par intention — analyser, présenter, solliciter —
 * l'entête redevient lisible. Un groupe dont tous les items sont indisponibles
 * ne s'affiche pas du tout : un menu vide est une promesse non tenue.
 */
export function menuHtml(id: string, label: string, items: MenuItem[], primary = false): string {
  const rows = items.filter(Boolean);
  if (!rows.length) return '';
  const entries = rows
    .map(
      (item) =>
        `<button id="${item.id}" type="button" role="menuitem" class="menu-hover-bg3"
          style="display:block;width:100%;text-align:left;padding:8px 12px;border:none;cursor:pointer;font-size:12px;color:var(--text)">${esc(item.label)}</button>`,
    )
    .join('');
  return `<div style="position:relative;display:inline-flex" data-menu-wrap="${id}">
      <button id="${id}" type="button" class="btn ${primary ? 'btn-primary' : 'btn-secondary'}"
        aria-haspopup="menu" aria-expanded="false">${esc(label)} ▾</button>
      <div id="${id}-menu" role="menu" style="display:none;position:absolute;top:calc(100% + 4px);right:0;
        background:var(--bg2);border:1px solid var(--border2);border-radius:var(--radius);
        box-shadow:0 8px 24px rgba(0,0,0,.15);min-width:220px;z-index:1000;overflow:hidden">${entries}</div>
    </div>`;
}

/** Ouverture / fermeture des menus, et fermeture au clic extérieur.
 *
 * Le disposer est enregistré auprès du routeur : sans lui, chaque navigation
 * empilerait un écouteur `document` de plus (audit de fuites P1 #9).
 */
export function bindMenus(ids: string[]): void {
  const closeAll = (): void => {
    ids.forEach((id) => {
      const box = document.getElementById(`${id}-menu`);
      if (box) box.style.display = 'none';
      document.getElementById(id)?.setAttribute('aria-expanded', 'false');
    });
  };
  ids.forEach((id) => {
    document.getElementById(id)?.addEventListener('click', (e) => {
      e.stopPropagation();
      const box = document.getElementById(`${id}-menu`);
      if (!box) return;
      const willOpen = box.style.display === 'none';
      closeAll();
      box.style.display = willOpen ? 'block' : 'none';
      document.getElementById(id)?.setAttribute('aria-expanded', String(willOpen));
    });
  });
  document.addEventListener('click', closeAll);
  registerPageDisposer(() => document.removeEventListener('click', closeAll));
}
